/** Aerial imagery sources (§12.1). No Google, anywhere (§2).
 *
 *  Every maxNativeZoom below was measured against each service's own /tilemap
 *  endpoint over Greenway Parc 3, Surprise (33.6186, -112.3950) on 2026-07-25.
 *  Guessing this number is exactly what painted the map gray in the field:
 *  USGS advertises 19 levels but holds no tile above 16 out there, and the app
 *  moves to zoom 19 after a search — so every single tile 404'd and the user
 *  got a blank box with no explanation.
 */
export interface BasemapDef {
  key: string;
  label: string;
  group: 'County aerial' | 'Nationwide';
  url: string;
  attribution: string;
  /** the deepest zoom the cache actually holds — verified, never assumed */
  maxNativeZoom: number;
  /** [[south, west], [north, east]] — outside this, no tiles are requested */
  bounds?: [[number, number], [number, number]];
  note?: string;
}

/** Highest zoom any layer is allowed to reach. Past maxNativeZoom Leaflet
 *  upscales the last real tile, which stays readable and never blanks. */
export const MAX_ZOOM = 22;

/** The Assessor flies the whole county every year and publishes the tile cache.
 *  z21 is 2.9 in/px — about four times sharper than any nationwide layer — and
 *  having several years lets you show an HOA what the ground looked like before.
 *  2021 and 2022 are cached too shallow to be useful here, so they aren't offered. */
const MC_YEARS = [2026, 2025, 2024, 2023];
const MC_BOUNDS: [[number, number], [number, number]] = [[32.25, -113.63], [34.40, -110.76]];

const countyMaps: BasemapDef[] = MC_YEARS.map((y) => ({
  key: `mc${y}`,
  label: `County ${y}`,
  group: 'County aerial',
  url: `https://gis.mcassessor.maricopa.gov/arcgis/rest/services/Imagery/Aerials${y}/MapServer/tile/{z}/{y}/{x}`,
  attribution: `Aerial imagery: Maricopa County Assessor, ${y} flight`,
  maxNativeZoom: 21,
  bounds: MC_BOUNDS,
  note: 'Maricopa County only',
}));

export const BASEMAPS: BasemapDef[] = [
  ...countyMaps,
  {
    key: 'esri', label: 'Nationwide aerial', group: 'Nationwide',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles © Esri — Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
    maxNativeZoom: 19,
    note: 'Works outside Maricopa County',
  },
  {
    key: 'usgs', label: 'USGS (public domain, less detail)', group: 'Nationwide',
    url: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Imagery: USGS The National Map (public domain)',
    // measured: no tiles above 16 over Surprise. Blurry beats blank.
    maxNativeZoom: 16,
  },
  {
    key: 'osm', label: 'Street map (no photo)', group: 'Nationwide',
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors',
    maxNativeZoom: 19,
  },
];

/** Drawn underneath the chosen layer so a missing tile shows imagery, not gray.
 *  Esri is the only nationwide source verified to hold tiles at working zoom here. */
export const FALLBACK_BASEMAP = 'esri';

/** items whose polygons render granite-gold (hardscape / groundcover); the rest render palo verde */
export const GRANITE_ITEMS = new Set(['dg', 'rock', 'paver', 'conc', 'grade', 'topdress', 'mulch']);

/** Is this point somewhere the county's own photos exist? Used to offer the
 *  sharp layer to anyone still sitting on a nationwide one. */
export const inCountyCoverage = (lat: number, lng: number): boolean =>
  lat >= MC_BOUNDS[0][0] && lat <= MC_BOUNDS[1][0] && lng >= MC_BOUNDS[0][1] && lng <= MC_BOUNDS[1][1];

export const DEFAULT_BASEMAP = 'mc2026';
export const basemapFor = (key: string): BasemapDef =>
  BASEMAPS.find((b) => b.key === key) ?? BASEMAPS.find((b) => b.key === FALLBACK_BASEMAP)!;
